import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { BookingLifecycle, UpdateVoucherDto, VoucherDto } from '@gty/common';

import { vouchersService } from '../services';
import { cn } from '../ui/cn';
import { Field, TextInput, selectAllOnFocus } from '../ui/Modal';
import { UndoableTextArea } from '../ui/UndoableTextArea';
import { Button } from '../ui/primitives';
import { describeSaveError, lockFor, type DocumentSaveError } from './document-lock';
import { DocumentEditHistory } from './DocumentEditHistory';
import {
  diffPassengers,
  DocumentPassengerFields,
  toPassengerDrafts,
  type PassengerDraft,
} from './DocumentPassengerFields';
import {
  blankTransportLegDraft,
  diffTransportLegs,
  toTransportLegDrafts,
  transportLegsBlockReason,
  VoucherTransportLegFields,
  type TransportLegDraft,
} from './VoucherTransportLegFields';
import {
  blankHotelDetailDraft,
  diffHotelDetails,
  hotelDetailsBlockReason,
  toHotelDetailDrafts,
  VoucherHotelDetailFields,
  type HotelDetailDraft,
} from './VoucherHotelDetailFields';
import { useInvalidateOn } from '../app/cache-invalidation';

/**
 * Item 2 — printed when a voucher has no cancellation policy text at all:
 * either the linked tour never had one set, or the voucher was issued before
 * this feature existed. Deliberately generic and short — it must never
 * misstate a specific window/percentage the business has not actually set.
 */
export const DEFAULT_CANCELLATION_POLICY_TEXT =
  "Cancellation charges are applicable on the total package cost as per the booking's agreed terms.";

/**
 * The ONE voucher for a booking, edited INLINE on the booking screen.
 *
 * No "generate" button anywhere — the voucher is raised by the payment that
 * reaches TOKEN_PAID, inside that payment's transaction, and simply appears.
 * No version picker either: one record, one download, always current.
 *
 * Editable indefinitely while the trip is ahead of it, INCLUDING after it has
 * been downloaded. It freezes only when the booking reaches COMPLETED (or is
 * cancelled), which the server enforces and this greys out in advance.
 *
 * Every field here is one the voucher OWNS — the joining instructions, and the
 * voucher's own passenger rows. The guest name, package and dates are snapshots
 * that belong to the booking and the invoice, and are not editable through this
 * contract.
 *
 * The passenger sub-form is what makes the flow doc's own example possible: a
 * voucher is raised at TOKEN_PAID, before any invoice exists, and until now it
 * borrowed its printed passenger table from the invoice or the booking, so there
 * was nothing document-owned to correct. It has its own rows now. Editing one
 * changes THIS document only — see `DocumentPassengerFields`.
 *
 * The "Hotel details" sub-form (item 5) and the "Transport" sub-form are both
 * fully editable row sets: rows are added, corrected and removed, and saved
 * as an id-keyed upsert plus explicit removals — see
 * `VoucherHotelDetailFields` and `VoucherTransportLegFields`. Hotel details
 * REPLACES the old "Stay / Services" card (`VoucherVendorFields`, which only
 * let staff edit a note on a fixed, snapshotted row set) as the one hotel
 * section on this screen.
 */
export function VoucherEditor({
  voucher,
  bookingStatus,
  canEdit,
}: {
  voucher: VoucherDto;
  bookingStatus: BookingLifecycle;
  /** BOOKING_VOUCHER:FULL — Owner, Admin and Tour Manager in the seeded matrix. */
  canEdit: boolean;
}): JSX.Element {
  const invalidateOn = useInvalidateOn();
  const [draft, setDraft] = useState(() => toDraft(voucher));
  const [saveError, setSaveError] = useState<DocumentSaveError | null>(null);
  const [saved, setSaved] = useState(false);

  /**
   * Re-seed the form when the SERVER's voucher changes identity or content.
   *
   * Keyed on `id` and `ref` rather than the whole object: a refetch returning an
   * equal-but-new object must not wipe what the user is currently typing.
   */
  useEffect(() => {
    setDraft(toDraft(voucher));
    setSaveError(null);
    setSaved(false);
  }, [voucher.id, voucher.ref]);

  /**
   * Re-seed ONLY the transport legs when the SERVER's set of legs changes.
   *
   * The effect above deliberately ignores content changes, but a saved new leg
   * comes back with a server id: left as an id-less draft, the next diff would
   * re-send it as another new leg AND remove the server's copy. Keyed on the id
   * list, so a refetch of the same legs does not touch what is being typed.
   */
  const legIdSignature = voucher.transportLegs.map((leg) => leg.id).join(',');
  useEffect(() => {
    setDraft((current) => ({
      ...current,
      transportLegs: toTransportLegDrafts(voucher.transportLegs),
    }));
  }, [legIdSignature]);

  /** Re-seed ONLY the hotel rows when the SERVER's set changes. Same reasoning as `legIdSignature`. */
  const hotelIdSignature = voucher.hotelDetails.map((row) => row.id).join(',');
  useEffect(() => {
    setDraft((current) => ({
      ...current,
      hotelDetails: toHotelDetailDrafts(voucher.hotelDetails),
    }));
  }, [hotelIdSignature]);

  /**
   * Locked by the booking's state, by the server's own `locked` flag, or by a
   * 409 that arrived while this form was open. Any one of them is enough.
   */
  const statusLock = lockFor('voucher', bookingStatus);
  const locked = statusLock.locked || voucher.locked || saveError?.locked === true;
  const lockReason =
    saveError?.locked === true
      ? saveError.message
      : (statusLock.reason ??
        'This voucher is locked and can no longer be edited.');

  const save = useMutation({
    mutationFn: (patch: UpdateVoucherDto) => vouchersService.update(voucher.id, patch),
    onSuccess: () => {
      setSaveError(null);
      setSaved(true);
      // The document panel and the printed preview both re-read from here.
      invalidateOn('document.edited');
    },
    onError: (err: unknown) => {
      setSaved(false);
      setSaveError(describeSaveError(err, 'voucher'));
    },
  });

  /**
   * Every field edits through here so the "Saved" confirmation clears itself.
   *
   * It must NOT be derived from `dirty` instead. After a successful save the
   * `voucher` prop is still the old one until the refetch lands, so the draft
   * legitimately differs from it for a moment — gating the confirmation on
   * `!dirty` made it appear only when the network happened to be quick, which is
   * the worst of both behaviours.
   */
  const edit = (changes: Partial<VoucherDraft>): void => {
    setDraft((current) => ({ ...current, ...changes }));
    setSaved(false);
  };

  /** One passenger row, by position in the document's printed order. */
  const editPassenger = (index: number, changes: Partial<PassengerDraft>): void => {
    setDraft((current) => ({
      ...current,
      passengers: current.passengers.map((p, i) => (i === index ? { ...p, ...changes } : p)),
    }));
    setSaved(false);
  };

  /** One hotel row, by its draft key (a new row has no server id yet). */
  const editHotelDetail = (key: string, changes: Partial<HotelDetailDraft>): void => {
    setDraft((current) => ({
      ...current,
      hotelDetails: current.hotelDetails.map((row) =>
        row.key === key ? { ...row, ...changes } : row,
      ),
    }));
    setSaved(false);
  };

  const addHotelDetail = (): void => {
    setDraft((current) => ({
      ...current,
      hotelDetails: [...current.hotelDetails, blankHotelDetailDraft()],
    }));
    setSaved(false);
  };

  /** Dropping a saved row from the drafts is what puts its id in `removeIds`. */
  const removeHotelDetail = (key: string): void => {
    setDraft((current) => ({
      ...current,
      hotelDetails: current.hotelDetails.filter((row) => row.key !== key),
    }));
    setSaved(false);
  };

  /** One transport leg, by its draft key (a new leg has no server id yet). */
  const editTransportLeg = (key: string, changes: Partial<TransportLegDraft>): void => {
    setDraft((current) => ({
      ...current,
      transportLegs: current.transportLegs.map((leg) =>
        leg.key === key ? { ...leg, ...changes } : leg,
      ),
    }));
    setSaved(false);
  };

  const addTransportLeg = (): void => {
    setDraft((current) => ({
      ...current,
      transportLegs: [...current.transportLegs, blankTransportLegDraft()],
    }));
    setSaved(false);
  };

  /** Dropping a saved leg from the drafts is what puts its id in `removeIds`. */
  const removeTransportLeg = (key: string): void => {
    setDraft((current) => ({
      ...current,
      transportLegs: current.transportLegs.filter((leg) => leg.key !== key),
    }));
    setSaved(false);
  };

  const patch = diffVoucher(voucher, draft);
  const dirty = Object.keys(patch).length > 0;
  /** Client-side mirror of the server's row rules — blocks the save and says why. */
  const blockReason = transportLegsBlockReason(draft.transportLegs) ?? hotelDetailsBlockReason(draft.hotelDetails);

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!dirty || locked || blockReason !== null) return;
    // Only the fields that actually changed. Sending the whole draft would write
    // every column on every save and fill the audit trail with noise, making a
    // real change impossible to find.
    save.mutate(patch);
  };

  return (
    <div>
      {locked && (
        <p
          role="status"
          className="border-b border-neutral-200 bg-neutral-100 px-5 py-3 text-[12px] leading-[1.5] text-neutral-700"
        >
          <strong>Locked.</strong> {lockReason}
        </p>
      )}

      {!locked && !canEdit && (
        <p className="border-b border-neutral-200 bg-neutral-100 px-5 py-3 text-[12px] leading-[1.5] text-neutral-700">
          You can view this voucher but not edit it. Editing is available to the Owner, Admin and
          Tour Manager.
        </p>
      )}

      <form onSubmit={submit}>
        <fieldset disabled={locked || !canEdit} className="border-0 p-0">
          {/*
            GUEST AND TOUR — auto-populated from the booking, its customer and
            its tour when the voucher was raised (see `payment-flow.service.ts`
            and the note on `VoucherDto`), and editable here for the one field
            that never has an automatic source (`guestAddress` — a customer
            record holds a city, not a postal address) or for the rare booking
            where the snapshot was wrong or incomplete.
          */}
          <div className="grid gap-4 border-b border-neutral-200 px-5 py-4 sm:grid-cols-2">
            <Field label="Lead guest name">
              <TextInput
                value={draft.guestName}
                onChange={(e) => {
                  edit({ guestName: e.target.value });
                }}
              />
            </Field>

            <Field label="Contact number">
              <TextInput
                value={draft.guestPhone}
                onChange={(e) => {
                  edit({ guestPhone: e.target.value });
                }}
              />
            </Field>

            <Field label="Email ID">
              <TextInput
                type="email"
                value={draft.guestEmail}
                onChange={(e) => {
                  edit({ guestEmail: e.target.value });
                }}
              />
            </Field>

            <Field label="Address" hint="Not captured on the customer record — fill in here.">
              <TextInput
                value={draft.guestAddress}
                onChange={(e) => {
                  edit({ guestAddress: e.target.value });
                }}
              />
            </Field>

            <Field label="Package name">
              <TextInput
                value={draft.packageName}
                onChange={(e) => {
                  edit({ packageName: e.target.value });
                }}
              />
            </Field>

            <Field label="Duration" hint='e.g. "6 Nights / 7 Days".'>
              <TextInput
                value={draft.duration}
                onChange={(e) => {
                  edit({ duration: e.target.value });
                }}
              />
            </Field>

            <Field label="Travel date">
              <TextInput
                type="date"
                value={draft.travelDate}
                onChange={(e) => {
                  edit({ travelDate: e.target.value });
                }}
              />
            </Field>

            <Field label="Return date">
              <TextInput
                type="date"
                value={draft.returnDate}
                onChange={(e) => {
                  edit({ returnDate: e.target.value });
                }}
              />
            </Field>

            <Field label="Departure city">
              <TextInput
                value={draft.departureCity}
                onChange={(e) => {
                  edit({ departureCity: e.target.value });
                }}
              />
            </Field>

            <Field label="Destination">
              <TextInput
                value={draft.destination}
                onChange={(e) => {
                  edit({ destination: e.target.value });
                }}
              />
            </Field>
          </div>

          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
            <Field label="Pickup point">
              <TextInput
                value={draft.pickupPoint}
                onChange={(e) => {
                  edit({ pickupPoint: e.target.value });
                }}
                placeholder="Dadar station, Platform 1 entrance"
              />
            </Field>

            <Field label="Vehicle details">
              <TextInput
                value={draft.vehicleDetails}
                onChange={(e) => {
                  edit({ vehicleDetails: e.target.value });
                }}
                placeholder="Innova Crysta · MH-01-AB-1234"
              />
            </Field>

            {/*
              Reporting and pickup times are FREE TEXT, not time inputs. The
              printed design carries values like "7:30 PM, 29 Jul 2026" and
              "one hour before departure" — a `<input type="time">` here would
              refuse half of what the business actually writes. The
              placeholder nudges staff toward the 12-hour AM/PM convention the
              rest of the product uses without restricting what can be typed.
            */}
            <Field label="Reporting time" hint="Printed exactly as written.">
              <TextInput
                value={draft.reportingTime}
                onChange={(e) => {
                  edit({ reportingTime: e.target.value });
                }}
                placeholder="7:30 PM, 29 Jul 2026"
              />
            </Field>

            <Field label="Pickup time" hint="Printed exactly as written.">
              <TextInput
                value={draft.pickupTime}
                onChange={(e) => {
                  edit({ pickupTime: e.target.value });
                }}
                placeholder="7:30 AM"
              />
            </Field>

            <Field label="Tour manager">
              <TextInput
                value={draft.tourManager}
                onChange={(e) => {
                  edit({ tourManager: e.target.value });
                }}
              />
            </Field>

            <Field label="Tour manager phone">
              <TextInput
                value={draft.tourManagerPhone}
                onChange={(e) => {
                  edit({ tourManagerPhone: e.target.value });
                }}
              />
            </Field>

            <Field label="Alternate number">
              <TextInput
                value={draft.alternateNumber}
                onChange={(e) => {
                  edit({ alternateNumber: e.target.value });
                }}
              />
            </Field>

            <Field label="Balance due date" hint="Leave empty once the balance is settled.">
              <TextInput
                type="date"
                value={draft.balanceDueDate}
                onChange={(e) => {
                  edit({ balanceDueDate: e.target.value });
                }}
              />
            </Field>

            <Field label="Adults">
              <TextInput
                type="number"
                min={0}
                value={draft.adults === '0' ? '' : draft.adults}
                onFocus={selectAllOnFocus}
                onChange={(e) => {
                  edit({ adults: e.target.value });
                }}
              />
            </Field>

            <Field label="Kids">
              <TextInput
                type="number"
                min={0}
                value={draft.kids}
                onFocus={selectAllOnFocus}
                onChange={(e) => {
                  edit({ kids: e.target.value });
                }}
              />
            </Field>

            <Field label="Package includes" hint="One per line.">
              <UndoableTextArea
                value={draft.includes}
                resetKey={voucher.id}
                onChange={(e) => {
                  edit({ includes: e.target.value });
                }}
              />
            </Field>

            <Field label="Package excludes" hint="One per line.">
              <UndoableTextArea
                value={draft.excludes}
                resetKey={voucher.id}
                onChange={(e) => {
                  edit({ excludes: e.target.value });
                }}
              />
            </Field>

            <Field
              label="Cancellation policy"
              hint="Leave empty to print the standard default. Editing this never changes the Tour Master's own policy."
            >
              <UndoableTextArea
                value={draft.cancellationPolicy}
                resetKey={voucher.id}
                onChange={(e) => {
                  edit({ cancellationPolicy: e.target.value });
                }}
                rows={4}
              />
            </Field>
          </div>

          {/*
            HOTEL DETAILS (item 5) — the voucher's own hotel-stay rows.
            REPLACES the old read-only "Stay / Services" card
            (`VoucherVendorFields`) as the one hotel section: staff asked for
            one section, not two overlapping ones, and this one is fully
            editable (add/correct/remove) where the old card only let them
            edit a note. `voucher.vendors`/`VoucherVendorFields` still exist
            in the codebase and the data is still prefilled server-side, but
            this editor no longer renders that card.
          */}
          <VoucherHotelDetailFields
            drafts={draft.hotelDetails}
            onChange={editHotelDetail}
            onAdd={addHotelDetail}
            onRemove={removeHotelDetail}
          />

          {/*
            TRANSPORT — the voucher's own legs. Always shown (with an add
            button) even when empty, unlike the old Stay / Services card:
            staff need a way to enter the first leg. Sits after Hotel
            Details, matching the printed sheet's page-2 order.
          */}
          <VoucherTransportLegFields
            drafts={draft.transportLegs}
            onChange={editTransportLeg}
            onAdd={addTransportLeg}
            onRemove={removeTransportLeg}
          />

          <DocumentPassengerFields
            document="voucher"
            drafts={draft.passengers}
            onChange={editPassenger}
          />

          {!locked && canEdit && (
            <div className="flex flex-wrap items-center gap-3 border-t border-neutral-200 px-5 py-3">
              <Button type="submit" disabled={!dirty || save.isPending || blockReason !== null}>
                {save.isPending ? 'Saving…' : 'Save voucher'}
              </Button>
              {dirty && !save.isPending && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDraft(toDraft(voucher));
                    setSaved(false);
                  }}
                >
                  Discard changes
                </Button>
              )}
              {saved && !save.isPending && (
                <span role="status" className="text-[12px] leading-none text-success-ink">
                  Saved. The download now renders these details.
                </span>
              )}
              {dirty && blockReason !== null && (
                <span role="status" className="text-[12px] leading-[1.4] text-danger-ink">
                  {blockReason}
                </span>
              )}
            </div>
          )}
        </fieldset>
      </form>

      {saveError !== null && saveError.locked === false && (
        <p
          role="alert"
          className={cn(
            'border-t border-danger-border bg-danger-bg px-5 py-3 text-[12px] leading-[1.5] text-danger-ink',
          )}
        >
          {saveError.message}
        </p>
      )}

      <DocumentEditHistory
        queryKey={['vouchers', 'edits', voucher.id]}
        fetchEdits={(signal) => vouchersService.edits(voucher.id, signal)}
      />
    </div>
  );
}

/**
 * The editable fields as FORM STRINGS.
 *
 * Numbers and the nullable date live as strings while being typed — an `adults`
 * field bound to a number cannot represent the empty box the user sees mid-edit,
 * and a date bound to `null` cannot either.
 */
interface VoucherDraft {
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  guestAddress: string;
  packageName: string;
  /** Lives as a string while typed, like `balanceDueDate` below. */
  travelDate: string;
  returnDate: string;
  duration: string;
  departureCity: string;
  destination: string;

  alternateNumber: string;
  adults: string;
  kids: string;
  pickupPoint: string;
  reportingTime: string;
  pickupTime: string;
  tourManager: string;
  tourManagerPhone: string;
  vehicleDetails: string;
  includes: string;
  excludes: string;
  balanceDueDate: string;
  /** Item 2 — `''` while typed means "print the default", same as `balanceDueDate` meaning "settled". */
  cancellationPolicy: string;
  /** The voucher's OWN rows — not the booking's and not the invoice's. */
  passengers: PassengerDraft[];
  /**
   * The voucher's OWN transport legs, as rows that can be added and removed.
   * A saved leg missing from here is a removal; see `diffTransportLegs`.
   */
  transportLegs: TransportLegDraft[];
  /**
   * The voucher's OWN hotel-stay rows (item 5), as rows that can be added
   * and removed. A saved row missing from here is a removal; see
   * `diffHotelDetails`.
   */
  hotelDetails: HotelDetailDraft[];
}

function toDraft(v: VoucherDto): VoucherDraft {
  return {
    guestName: v.guestName,
    guestPhone: v.guestPhone,
    guestEmail: v.guestEmail,
    guestAddress: v.guestAddress,
    packageName: v.packageName,
    travelDate: v.travelDate ?? '',
    returnDate: v.returnDate ?? '',
    duration: v.duration,
    departureCity: v.departureCity,
    destination: v.destination,

    alternateNumber: v.alternateNumber,
    adults: String(v.adults),
    kids: String(v.kids),
    pickupPoint: v.pickupPoint,
    reportingTime: v.reportingTime,
    pickupTime: v.pickupTime,
    tourManager: v.tourManager,
    tourManagerPhone: v.tourManagerPhone,
    vehicleDetails: v.vehicleDetails,
    includes: v.includes.join('\n'),
    excludes: v.excludes.join('\n'),
    balanceDueDate: v.balanceDueDate ?? '',
    cancellationPolicy: v.cancellationPolicy ?? '',
    passengers: toPassengerDrafts(v.passengers),
    transportLegs: toTransportLegDrafts(v.transportLegs),
    hotelDetails: toHotelDetailDrafts(v.hotelDetails),
  };
}

function toLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * ONLY WHAT CHANGED.
 *
 * Exported for its own test. Two reasons it matters beyond payload size: the
 * server writes one audit row per save listing the field names, so sending
 * everything would record twelve changes for a one-word fix and bury the real
 * edit; and the server returns early with no audit row at all when the patch is
 * empty, which is the behaviour a no-op save should have.
 */
export function diffVoucher(original: VoucherDto, draft: VoucherDraft): UpdateVoucherDto {
  const patch: UpdateVoucherDto = {};

  if (draft.guestName !== original.guestName) patch.guestName = draft.guestName;
  if (draft.guestPhone !== original.guestPhone) patch.guestPhone = draft.guestPhone;
  if (draft.guestEmail !== original.guestEmail) patch.guestEmail = draft.guestEmail;
  if (draft.guestAddress !== original.guestAddress) patch.guestAddress = draft.guestAddress;
  if (draft.packageName !== original.packageName) patch.packageName = draft.packageName;
  if (draft.duration !== original.duration) patch.duration = draft.duration;
  if (draft.departureCity !== original.departureCity) patch.departureCity = draft.departureCity;
  if (draft.destination !== original.destination) patch.destination = draft.destination;

  // Emptied dates are deliberate NULLs, same treatment as `balanceDueDate`
  // below — the draft's '' means "clear it", not "leave it".
  const travelDate = draft.travelDate === '' ? null : draft.travelDate;
  if (travelDate !== (original.travelDate ?? null)) patch.travelDate = travelDate;
  const returnDate = draft.returnDate === '' ? null : draft.returnDate;
  if (returnDate !== (original.returnDate ?? null)) patch.returnDate = returnDate;

  if (draft.alternateNumber !== original.alternateNumber) {
    patch.alternateNumber = draft.alternateNumber;
  }
  if (draft.pickupPoint !== original.pickupPoint) patch.pickupPoint = draft.pickupPoint;
  if (draft.reportingTime !== original.reportingTime) patch.reportingTime = draft.reportingTime;
  if (draft.pickupTime !== original.pickupTime) patch.pickupTime = draft.pickupTime;
  if (draft.tourManager !== original.tourManager) patch.tourManager = draft.tourManager;
  if (draft.tourManagerPhone !== original.tourManagerPhone) {
    patch.tourManagerPhone = draft.tourManagerPhone;
  }
  if (draft.vehicleDetails !== original.vehicleDetails) patch.vehicleDetails = draft.vehicleDetails;

  const adults = Number(draft.adults);
  if (draft.adults !== '' && Number.isFinite(adults) && adults !== original.adults) {
    patch.adults = adults;
  }
  const kids = Number(draft.kids);
  if (draft.kids !== '' && Number.isFinite(kids) && kids !== original.kids) {
    patch.kids = kids;
  }

  const includes = toLines(draft.includes);
  if (!sameList(includes, original.includes)) patch.includes = includes;
  const excludes = toLines(draft.excludes);
  if (!sameList(excludes, original.excludes)) patch.excludes = excludes;

  // An emptied date is a deliberate NULL — the design hides the line once the
  // balance is settled — so it is sent rather than treated as "unchanged".
  const due = draft.balanceDueDate === '' ? null : draft.balanceDueDate;
  if (due !== (original.balanceDueDate ?? null)) patch.balanceDueDate = due;

  // Item 2 — same "empty box means a deliberate null" treatment: clearing
  // this field is how staff reset a voucher back to the printed default,
  // same reasoning as `balanceDueDate` above. The server trims and nullifies
  // whitespace-only text on its own, so the diff only needs the exact
  // empty-string sentinel.
  const cancellationPolicy = draft.cancellationPolicy === '' ? null : draft.cancellationPolicy;
  if (cancellationPolicy !== (original.cancellationPolicy ?? null)) {
    patch.cancellationPolicy = cancellationPolicy;
  }

  // Only the passenger ROWS that changed, and only their changed fields. The
  // key is omitted entirely when nothing did, so a save that touched no
  // passenger cannot appear in the edit trail as if it had.
  const passengers = diffPassengers(original.passengers, draft.passengers);
  if (passengers.length > 0) patch.passengers = passengers;

  // Transport legs: new and corrected legs in `upsert`, removed ones in
  // `removeIds`, unchanged ones not at all — and the key omitted entirely when
  // no leg changed.
  const transportLegs = diffTransportLegs(original.transportLegs, draft.transportLegs);
  if (transportLegs !== null) patch.transportLegs = transportLegs;

  // Hotel details (item 5): same id-keyed upsert/remove shape as transport legs.
  const hotelDetails = diffHotelDetails(original.hotelDetails, draft.hotelDetails);
  if (hotelDetails !== null) patch.hotelDetails = hotelDetails;

  return patch;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}
